/**
 * ============================================================================
 * TABLE SELECTORS — derive table/tile views from getMetrics + the catalog.
 * ============================================================================
 *
 * The QuickSight WBR dashboard renders every visual (KPI tiles, network
 * summary tables, OTD tables) from ONE dataset. This module is the custom-UI
 * equivalent: all views derive from the same GetMetricsOutput, joined to the
 * getMetricDefinitions catalog by metricKey.
 *
 * - selectWeekColumns:     periods → ordered week columns with period group
 * - selectMetricTableRows: metric × week pivot (rows for MetricTableSection)
 * - selectNetworkTiles:    per-network rollup for the KPI tiles page
 *
 * Row identity and ordering come from the catalog (metricKey, sortOrder);
 * aggregation comes from aggregationCore. Neither is re-implemented here.
 */

import {
  GetMetricsOutput,
  DcrSnapshotData,
  DcrRecord,
  MetricDefinition,
} from 'src/types/metricsApi.types';
import {
  MetricCatalog,
  accumulateMetric,
  aggregateMetric,
  buildMetricCatalog,
  finalizeRate,
  findEntry,
  meanOf,
  normalizeMetricType,
  numericValues,
  parseReportingPeriod,
  metricUnit,
  perDcrRate,
  percentileDisc,
} from 'src/features/api/aggregationCore';
import {
  matchesDemandCategory,
  DEMAND_CATEGORIES,
} from 'src/config/demandCategories';
import {
  MetricGroupSelection,
  MetricNameSelection,
  metricGroupMatches,
  metricNameMatches,
} from 'src/features/api/filterPredicate';

/** Optional row-level predicate applied to DCRs before aggregation. */
export type DcrPredicate = (dcr: DcrRecord) => boolean;

// ──── Output types ────

export type PeriodGroup = 'historical' | 'current' | 'forward';

/**
 * Column-group display titles ("Forward", not "Forecast"). No "1./2./3."
 * numbering: the QuickSight originals carried it only to force an ordering
 * that the column layout already expresses.
 */
export const PERIOD_GROUP_LABELS: Record<PeriodGroup, string> = {
  historical: 'Historical Period',
  current: 'Current Period',
  forward: 'Forward Period',
};

export interface WeekColumn {
  /** Stable column key, e.g. "2026Week32". */
  key: string;
  /** Display label, e.g. "2026 Week 32". */
  label: string;
  group: PeriodGroup;
}

export interface MetricTableRow {
  /** Display name from the catalog (section configs match on this). */
  metric: string;
  /** Stable identity from the catalog ('' for client-derived rows). */
  metricKey: string;
  owner: string;
  goal: number;
  /** "GTE" = higher is better (default), "LTE" = lower is better. */
  goalDirection: string;
  /** True → render as percentage with RAG colouring vs goal. */
  isRate: boolean;
  /**
   * Display unit for values/goal — the catalog's `unit` with type-derived
   * fallback (metricUnit): "%" for rates, "days" for Launch TAT, '' when the
   * metric has no natural unit.
   */
  unit: string;
  /** Client-derived row (e.g. %DCRs≥threshold) — no callout panel. */
  derived?: boolean;
  /** WBR callout text ('' = none) — shown in the snapshot Callout column. */
  callout: string;
  /**
   * True when this is a VOLUME metric with a baseMetricKey: the cell shows the
   * raw sum, but the trend + Δ WoW plot ATTAINMENT (100 × metric/base), so the
   * sparkline is meaningful against the 95% goal — parity with the home cards.
   * The per-week attainment lives under `attnKey(weekKey)`.
   */
  attainmentTrend?: boolean;
  /** Week key → aggregated value (null = no data for that week). */
  [weekKey: string]: number | string | boolean | null | undefined;
}

/** Parallel key holding a volume row's per-week attainment (see attainmentTrend). */
export const attnKey = (weekKey: string): string => `attn:${weekKey}`;
/**
 * Parallel keys for a derived headline row's per-week caption (a second line
 * under the value naming the stage it came from) and tooltip (the per-stage
 * breakdown). Same shape as attnKey: the week's value stays the number, so
 * trend, delta and export are unaffected.
 */
export const captionKey = (weekKey: string): string => `caption:${weekKey}`;
export const tooltipKey = (weekKey: string): string => `tooltip:${weekKey}`;

export interface NetworkTileMetric {
  name: string;
  isOnTrack: boolean;
  value?: number;
  target?: number;
  unit?: string;
}

export interface NetworkTile {
  id: string;
  name: string;
  metrics: NetworkTileMetric[];
  /** Aggregated primary rate (first percentage metric) for threshold colour. */
  primaryRatePct: number;
}

// ──── Shared helpers ────

function dcrsForNetwork(
  period: DcrSnapshotData,
  networkSlug?: string,
  predicate?: DcrPredicate,
): DcrRecord[] {
  let dcrs = period.dcrData;
  if (networkSlug && networkSlug !== 'all') {
    // Membership from the network registry (src/config/demandCategories.ts) —
    // the single source of truth; supports rollups (category lists).
    dcrs = dcrs.filter((dcr) =>
      matchesDemandCategory(networkSlug, dcr.demandCategory),
    );
  }
  return predicate ? dcrs.filter(predicate) : dcrs;
}

/** Each period's DCRs filtered once, indexed like response.data; the metric x week loops reuse them. */
function filteredPeriods(
  response: GetMetricsOutput,
  networkSlug?: string,
  predicate?: DcrPredicate,
): DcrRecord[][] {
  return response.data.map((period) =>
    dcrsForNetwork(period, networkSlug, predicate),
  );
}

/**
 * Catalog metrics that appear in the data, in catalog (sortOrder) order,
 * restricted to the selected Metric Groups. With `requirePresence` false the
 * data-presence check is skipped — the catalog alone decides the rows (the
 * Launch TAT "skeleton rule": rows never disappear on empty weeks).
 */
function presentDefinitions(
  periods: DcrRecord[][],
  definitions: MetricDefinition[],
  metricGroups?: MetricGroupSelection,
  metricNames?: MetricNameSelection,
  requirePresence = true,
): MetricDefinition[] {
  const present = new Set<string>();
  if (requirePresence) {
    for (const dcrs of periods) {
      for (const dcr of dcrs) {
        for (const entry of dcr.metrics) present.add(entry.metricKey);
      }
    }
  }
  // Metric Group and Metric Name both filter EVERY row type, percentage (OTD)
  // rows included. The legacy dashboard's "OTD visuals ignore the group
  // control" behaviour was a QuickSight limitation, not a feature — data
  // always follows the filters here. The "WBR Metrics Only" chip matches by
  // preset membership (metricGroupMatches).
  return (
    definitions
      .filter(
        (definition) => !requirePresence || present.has(definition.metricKey),
      )
      // AVERAGE / PERCENTILE metrics (Launch TAT) are computed by a dedicated
      // selector — never SUM'd into the generic tables.
      .filter((definition) => !hasSpecialAggregation(definition))
      .filter((definition) => metricGroupMatches(definition, metricGroups))
      .filter((definition) => metricNameMatches(definition.name, metricNames))
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
  );
}

// ──── Derived OTD threshold metrics (spec: derived-otd-threshold-metrics) ────
// QuickSight computes "% DCRs with OTD >= Threshold" as calculated fields over
// the per-DCR OTD rates vs the OTDThreshold parameter (default 95 — CTS
// quicksightStack.ts). The per-DCR rates arrive as their own catalog metrics
// (per_dcr_otd_*) which are value carriers: never rows, never averaged.

/** QuickSight OTDThreshold parameter default (staticValues: [95.0]). */
export const OTD_THRESHOLD_DEFAULT = 95;

interface DerivedOtdFamily {
  /** Per-DCR rate carrier metricKey (0-100 capped, one entry per DCR). */
  carrierKey: string;
  /** The network OTD row this derived row follows in the table. */
  networkRowKey: string;
  /** Derived row label — exactly as the QuickSight visual labels it. */
  derivedRow: string;
  /** Fixed per-DCR threshold; when set the family ignores the OTD Threshold chip. */
  fixedThreshold?: number;
}

export const DERIVED_OTD_FAMILIES: DerivedOtdFamily[] = [
  {
    carrierKey: 'per_dcr_otd_against_requested_demand_nbd',
    networkRowKey: 'otd_against_requested_demand_nbd',
    derivedRow:
      '% DCRs with On Time Delivery >= Threshold (vs Requested Demand & NBD)',
  },
  {
    carrierKey: 'per_dcr_otd_against_committed_volume',
    networkRowKey: 'otd_against_committed_volume',
    derivedRow:
      '% DCRs with On Time Delivery >= Threshold (vs Committed Volume, New Data, Customer-Driven Date)',
  },
  {
    carrierKey: 'per_dcr_otd_against_ingested_committed_volume',
    networkRowKey: 'otd_against_ingested_committed_volume',
    derivedRow:
      '% DCRs with On Time Delivery >= Threshold (vs Ingested Committed Volume, New Data, Customer-Driven Date)',
  },
  // Starred "(Backlog + New Data)" families (backend spec
  // otd-new-data-and-customer-driven-variants): these preserve the pre-New-Data
  // math — total commitment including backlog, current plan's date. Their
  // per_dcr_* keys are PER_DCR_RATIO carriers exactly like the three above;
  // without these entries they would leak into tables/tiles as rows showing a
  // weighted network rate under a "% DCRs" label.
  {
    carrierKey: 'per_dcr_otd_against_ingested_commitment_backlog_and_new_data',
    networkRowKey: 'otd_against_ingested_total_commitment_backlog_and_new_data',
    derivedRow:
      '% DCRs with On Time Delivery >= Threshold (vs Ingested Commitment, Backlog + New Data)',
  },
  {
    carrierKey: 'per_dcr_otd_against_ingested_committed_backlog_and_new_data',
    networkRowKey: 'otd_against_committed_backlog_and_new_data',
    derivedRow:
      '% DCRs with On Time Delivery >= Threshold (vs Committed, Backlog + New Data)',
  },
  // Processing Accuracy (spec processing-accuracy-rows): fixed 95, not the
  // chip — the Kompass definition does not move.
  {
    carrierKey: 'per_dcr_processing_accuracy',
    networkRowKey: 'processing_accuracy_dcr_volume',
    derivedRow: 'Processing Accuracy (DCR Count >= 95%)',
    fixedThreshold: 95,
  },
];

/**
 * Table-only metrics (l1-quality-score placement decision): the L1 quality
 * trio's home is the DCR Health table section — they are deliberately NOT
 * home-page cards in either mode ("we don't want L1 on the summary page").
 * The home-card selector (selectWbrDashboardData) excludes these keys
 * unconditionally.
 */
export const TABLE_ONLY_METRIC_KEYS = new Set([
  'l1_quality_score',
  'l1_audited_volume',
  'l1_passed_volume',
]);

/** Per-DCR OTD rate carriers: never rendered anywhere, never aggregated. */
export const OTD_CARRIER_KEYS = new Set(
  DERIVED_OTD_FAMILIES.map((family) => family.carrierKey),
);

// ──── Pipeline Bottleneck (spec: pipeline-bottleneck-section) ────
// The four stage drop-offs are ordinary catalog RATIO_OF_SUMS rows. The
// headline row above them is client-derived: the worst stage of the week,
// captioned with the stage that owns it.

/**
 * Definition-only catalog key (like dsl_coverage_pct): no value is ever
 * published for it. It exists to give the derived row a stable metricKey plus
 * a catalog name and owner.
 */
export const PIPELINE_BOTTLENECK_KEY = 'pipeline_bottleneck';
/** Presentation fallbacks used when the catalog has no definition yet. */
export const PIPELINE_BOTTLENECK_NAME = 'Pipeline Bottleneck';
export const PIPELINE_BOTTLENECK_OWNER = 'robgill';

export interface PipelineDropoffStage {
  /** Catalog metricKey of the stage's drop-off % row. */
  metricKey: string;
  /** Caption/tooltip label for the stage transition. */
  stageLabel: string;
}

/**
 * The stage transitions in pipeline order. Array order IS the tie-break: a tie
 * for the worst stage reports the upstream-most one, because that is the
 * earliest break in the pipeline.
 */
export const PIPELINE_DROPOFF_STAGES: PipelineDropoffStage[] = [
  {
    metricKey: 'dropoff_demand_to_committed',
    stageLabel: 'Requested Demand → Committed',
  },
  {
    metricKey: 'dropoff_committed_to_ingested',
    stageLabel: 'Committed → Ingested',
  },
  {
    metricKey: 'dropoff_ingested_to_processed',
    stageLabel: 'Ingested → Processed',
  },
  {
    metricKey: 'dropoff_ingested_to_delivered',
    stageLabel: 'Ingested → Delivered',
  },
];
export const PIPELINE_DROPOFF_STAGE_KEYS: ReadonlySet<string> = new Set(
  PIPELINE_DROPOFF_STAGES.map((stage) => stage.metricKey),
);

// ──── Launch TAT (spec launch-tat-metrics) ────
// AVERAGE / PERCENTILE catalog metrics (Launch TAT, Controlled Launch TAT) are
// per-DCR day counts rendered in the DCR Health section by selectLaunchTatRows,
// not the generic SUM paths. The catalog decides which rows exist.
/** Default percentile for the (TP) rows — TP95 (backend default). */
export const DEFAULT_LAUNCH_TAT_PERCENTILE = 95;

/**
 * A metric whose weekly value is NOT a SUM (AVERAGE / PERCENTILE). These are
 * computed by dedicated selectors, so they must be kept OUT of the generic
 * SUM/rate paths (selectMetricTableRows, selectNetworkTiles) — a SUM of
 * calendar-day counts is meaningless.
 */
function hasSpecialAggregation(definition: MetricDefinition): boolean {
  return (
    definition.aggregationType === 'AVERAGE' ||
    definition.aggregationType === 'PERCENTILE'
  );
}

// ──── Selectors ────

export function selectWeekColumns(
  response: GetMetricsOutput | undefined,
): WeekColumn[] {
  if (!response) return [];
  return response.data.map((period) => {
    const { key, label } = parseReportingPeriod(period.reportingPeriod);
    const group: PeriodGroup =
      period.timeline === 'CURRENT'
        ? 'current'
        : period.timeline === 'FUTURE'
        ? 'forward'
        : 'historical';
    return { key, label, group };
  });
}

/**
 * Pivot metric × week. `types` filters which metric kinds appear (the
 * Executive Summary "volumes" section vs the "OTD %" section); omit for all.
 */
export function selectMetricTableRows(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  options: {
    types?: Array<'percentage' | 'volume' | 'count'>;
    network?: string;
    predicate?: DcrPredicate;
    /** OTDThreshold parameter for derived %DCRs rows (default 95). */
    otdThreshold?: number;
    /** Metric Group chips — filters metric ROWS (not DCR rows). */
    metricGroups?: MetricGroupSelection;
    /** Metric Name chips — explicit metric picks, applies to every row type. */
    metricNames?: MetricNameSelection;
    /**
     * Default true: a metric rows only when some loaded DCR carries it.
     * False = catalog-skeleton rule (same as Launch TAT): every catalog
     * definition rows, cells null ("–") on weeks with no data — a row never
     * disappears just because the window is empty. Callers that pass false
     * are expected to restrict the rows themselves (e.g. the DCR Health
     * section's configured metric list).
     */
    requirePresence?: boolean;
  } = {},
): MetricTableRow[] {
  if (!response || response.data.length === 0 || !definitions?.length)
    return [];
  const {
    types,
    network,
    predicate,
    metricGroups,
    metricNames,
    requirePresence,
  } = options;
  const otdThreshold = options.otdThreshold ?? OTD_THRESHOLD_DEFAULT;
  const catalog: MetricCatalog = buildMetricCatalog(definitions);
  const weekColumns = selectWeekColumns(response);
  const periods = filteredPeriods(response, network, predicate);

  const result: MetricTableRow[] = [];
  for (const definition of presentDefinitions(
    periods,
    definitions,
    metricGroups,
    metricNames,
    requirePresence,
  )) {
    const type = normalizeMetricType(definition.type);
    if (types && !types.includes(type)) continue;
    // Per-DCR OTD rates are value carriers for the derived %DCRs rows —
    // never table rows, never averaged (spec derived-otd R3).
    if (OTD_CARRIER_KEYS.has(definition.metricKey)) continue;
    // Definition-only row: it lends the derived Pipeline Bottleneck row its
    // name/owner and never carries a value. Presence alone excludes it, but
    // requirePresence:false would otherwise duplicate the derived row.
    if (definition.metricKey === PIPELINE_BOTTLENECK_KEY) continue;

    const row: MetricTableRow = {
      metric: definition.name,
      metricKey: definition.metricKey,
      owner: definition.owner ?? '',
      goal: parseFloat(definition.goal ?? '0') || 0,
      goalDirection: (definition.goalDirection ?? 'GTE').toUpperCase(),
      isRate: type === 'percentage',
      unit: metricUnit(definition),
      // Callouts come from the ListMetricComments API, not getMetrics.
      callout: '',
    };
    // A volume metric measured against a base (e.g. Committed vs Demand) keeps
    // its raw sum in the cell, but also carries a per-week attainment series
    // for the trend — same math as the home cards (finalizeRate(SUM, SUM)).
    const base =
      type !== 'percentage' && definition.baseMetricKey
        ? catalog.get(definition.baseMetricKey)
        : undefined;
    if (base) row.attainmentTrend = true;

    periods.forEach((dcrs, weekIndex) => {
      const weekKey = weekColumns[weekIndex].key;
      row[weekKey] = aggregateMetric(dcrs, definition.metricKey, catalog).value;
      if (base) {
        const num = accumulateMetric(dcrs, definition.metricKey, catalog);
        const den = accumulateMetric(dcrs, base.metricKey, catalog);
        row[attnKey(weekKey)] =
          num.seen && den.seen ? finalizeRate(num.sum, den.sum) : null;
      }
    });
    result.push(row);
  }

  // Derived "% DCRs with OTD >= Threshold" rows (QuickSight calculated-field
  // parity): 100 × count(perDcrRate ≥ threshold) / count(carrying DCRs) per
  // week over the SAME filtered slice; weeks with no carriers stay null.
  // Rows insert directly after their family's network OTD row (QS order).
  // A derived row FOLLOWS its family's network OTD row: it renders only when
  // that row's definition passes the Metric Group AND Metric Name selections.
  // So picking "Metric Name = On Time Delivery % Against Committed Volume"
  // keeps that row plus its own %DCRs companion, and picking "Demand" shows
  // no derived rows at all. (With no selections, a missing catalog definition
  // does not hide the family — carriers alone can still produce the row.)
  const networkRowOf = (family: DerivedOtdFamily) =>
    definitions.find(
      (definition) => definition.metricKey === family.networkRowKey,
    );
  const familySelected = (family: DerivedOtdFamily): boolean => {
    if (!metricGroups && !metricNames) return true;
    const networkRow = networkRowOf(family);
    const groupOk =
      !metricGroups ||
      (networkRow !== undefined &&
        metricGroupMatches(networkRow, metricGroups));
    const nameOk = metricNameMatches(networkRow?.name, metricNames);
    return groupOk && nameOk;
  };
  if (!types || types.includes('percentage')) {
    for (const family of DERIVED_OTD_FAMILIES.filter(familySelected)) {
      const derivedRow: MetricTableRow = {
        metric: family.derivedRow,
        // Anchor on the family's per-DCR carrier key: a real catalog key that
        // never renders as a row itself (OTD_CARRIER_KEYS guard above), so it
        // is free to give this derived row a stable identity — which makes it
        // selectable for callouts (spec metric-callouts R1.3).
        metricKey: family.carrierKey,
        // Fixed-threshold families sit in a per-row-owner section, so they
        // borrow the network row's owner; OTD rows are unchanged.
        owner:
          family.fixedThreshold === undefined
            ? ''
            : networkRowOf(family)?.owner ?? '',
        goal: 0,
        goalDirection: 'GTE',
        isRate: true,
        unit: '%',
        derived: true,
        callout: '',
      };
      const threshold = family.fixedThreshold ?? otdThreshold;
      let seen = false;
      periods.forEach((dcrs, weekIndex) => {
        let carrying = 0;
        let hits = 0;
        for (const dcr of dcrs) {
          const entry = findEntry(dcr, family.carrierKey);
          if (!entry) continue;
          const rate = perDcrRate(entry);
          if (rate === null) continue;
          carrying += 1;
          if (rate >= threshold) hits += 1;
        }
        derivedRow[weekColumns[weekIndex].key] =
          carrying > 0 ? (100 * hits) / carrying : null;
        if (carrying > 0) seen = true;
      });
      // No carriers anywhere → no row (never a row of zeros)… unless the
      // caller asked for the catalog-skeleton rule (requirePresence: false):
      // then the derived row renders like its network row, all cells "–".
      if (!seen && requirePresence !== false) continue;
      const familyIndex = result.findIndex(
        (row) => row.metricKey === family.networkRowKey,
      );
      result.splice(
        familyIndex >= 0 ? familyIndex + 1 : result.length,
        0,
        derivedRow,
      );
    }
  }

  // Derived "Pipeline Bottleneck" headline row (spec pipeline-bottleneck-
  // section): per week, the worst of the four stage drop-offs over the SAME
  // filtered slice, captioned with the stage that owns it.
  if (!types || types.includes('percentage')) {
    const stageDefinition = (stage: PipelineDropoffStage) =>
      definitions.find((d) => d.metricKey === stage.metricKey);
    // Same rule as the derived OTD rows' familySelected: this row heads its
    // stage rows, so it renders only while at least one of them is picked.
    const stageSelected = (stage: PipelineDropoffStage): boolean => {
      if (!metricGroups && !metricNames) return true;
      const definition = stageDefinition(stage);
      const groupOk =
        !metricGroups ||
        (definition !== undefined &&
          metricGroupMatches(definition, metricGroups));
      return groupOk && metricNameMatches(definition?.name, metricNames);
    };
    if (PIPELINE_DROPOFF_STAGES.some(stageSelected)) {
      const headline = definitions.find(
        (d) => d.metricKey === PIPELINE_BOTTLENECK_KEY,
      );
      const bottleneckRow: MetricTableRow = {
        metric: headline?.name ?? PIPELINE_BOTTLENECK_NAME,
        metricKey: PIPELINE_BOTTLENECK_KEY,
        owner: headline?.owner ?? PIPELINE_BOTTLENECK_OWNER,
        // No goal: goalOnScale(0) is false, so the cell stays neutral.
        goal: 0,
        goalDirection: 'GTE',
        isRate: true,
        unit: '%',
        derived: true,
        callout: '',
      };
      let seen = false;
      periods.forEach((dcrs, weekIndex) => {
        const weekKey = weekColumns[weekIndex].key;
        let worst: number | null = null;
        let worstLabel = '';
        const breakdown: string[] = [];
        for (const stage of PIPELINE_DROPOFF_STAGES) {
          // The generic RATIO_OF_SUMS path: 100 × SUM(num)/SUM(den), null on a
          // zero denominator. Not re-derived here.
          const { value } = aggregateMetric(dcrs, stage.metricKey, catalog);
          if (value === null) continue;
          breakdown.push(`${stage.stageLabel} ${value.toFixed(1)}%`);
          // Strict ">" leaves a tie with the first (upstream-most) stage.
          if (worst === null || value > worst) {
            worst = value;
            worstLabel = stage.stageLabel;
          }
        }
        bottleneckRow[weekKey] = worst;
        if (worst !== null) {
          bottleneckRow[captionKey(weekKey)] = worstLabel;
          bottleneckRow[tooltipKey(weekKey)] = breakdown.join(' · ');
          seen = true;
        }
      });
      // No stage has data in any week → no row, unless the caller asked for
      // the catalog-skeleton rule (then it renders with every cell "–").
      // The headline sits above the first stage row that is present, so a
      // Metric Name pick that drops stage 1 still keeps it above its stages.
      if (seen || requirePresence === false) {
        const firstStageIndex = result.findIndex((row) =>
          PIPELINE_DROPOFF_STAGE_KEYS.has(row.metricKey),
        );
        result.splice(
          firstStageIndex >= 0 ? firstStageIndex : result.length,
          0,
          bottleneckRow,
        );
      }
    }
  }

  return result;
}

// ──── Derived DSL Coverage rows (spec: dsl-coverage-snapshot) ────
// QuickSight's snapshot sheet shows a "{Category} - DSL Coverage" pivot per
// demand category, computed as calculated fields over the frozen
// collection_owner column (CTS quicksightStack.ts: DSL_Coverage_Pct,
// Avg_DCRs_Per_DSL). Neither is a catalog metric — like the %DCRs≥threshold
// rows, they derive client-side from the DCR records. Snapshot-only:
// collection_owner is captured at snapshot time, so LIVE data may not carry it.

/**
 * Catalog metricKeys for the definition-only DSL entries. When the catalog
 * carries them, their name/owner/goal/goalDirection win; otherwise the
 * constants below apply (QuickSight visual-subtitle parity). No values are
 * ever published for these keys, so they can never appear as catalog rows
 * in the regular sections (presentDefinitions requires data presence).
 */
export const DSL_COVERAGE_PCT_KEY = 'dsl_coverage_pct';
export const AVG_DCRS_PER_DSL_KEY = 'avg_dcrs_per_dsl';

/** QuickSight goal: DSL Coverage % ≥ 95 (GTE). */
export const DSL_COVERAGE_GOAL = 95;
/** QuickSight goal: Avg DCRs per DSL ≤ 7 (LTE). */
export const AVG_DCRS_PER_DSL_GOAL = 7;
/** Section owner, as the QuickSight visual subtitle shows it. */
export const DSL_COVERAGE_OWNER = 'robgill';

/** An assigned owner is non-null and non-blank (QS distinct_count skips NULL). */
function dslOwnerOf(dcr: DcrRecord): string | null {
  const owner = dcr.collectionOwner?.trim();
  return owner ? owner : null;
}

/** Presentation for one derived DSL row: catalog entry first, fallback second. */
function dslRowPresentation(
  definitions: MetricDefinition[] | undefined,
  metricKey: string,
  fallback: {
    metric: string;
    goal: number;
    goalDirection: string;
    unit: string;
  },
): Pick<
  MetricTableRow,
  'metric' | 'metricKey' | 'owner' | 'goal' | 'goalDirection' | 'unit'
> {
  const definition = definitions?.find((d) => d.metricKey === metricKey);
  if (!definition) {
    return { ...fallback, metricKey: '', owner: DSL_COVERAGE_OWNER };
  }
  return {
    metric: definition.name,
    metricKey: definition.metricKey,
    owner: definition.owner ?? DSL_COVERAGE_OWNER,
    goal: parseFloat(definition.goal ?? '') || fallback.goal,
    goalDirection: (
      definition.goalDirection ?? fallback.goalDirection
    ).toUpperCase(),
    unit: definition.unit ?? fallback.unit,
  };
}

/**
 * Derive the two DSL Coverage rows for one network scope, per week:
 *
 *   DSL Coverage %   = 100 × distinct DCRs with owner / distinct DCRs
 *   Avg DCRs per DSL = distinct DCRs with owner / distinct owners
 *
 * QuickSight parity (calculated fields over the snapshot dataset): weeks with
 * no DCRs (or, for the average, no owners) are null, never 0. Computed over
 * the SAME filtered slice as every other cell, so filter chips propagate.
 * Returns [] when no week has any DCR — the section shows its empty state.
 *
 * Presentation is catalog-first: the dsl_coverage_pct / avg_dcrs_per_dsl
 * entries supply name/owner/goal when present in the passed catalog (values
 * stay client-derived — the backend never publishes them).
 */
export function selectDslCoverageRows(
  response: GetMetricsOutput | undefined,
  options: {
    network?: string;
    predicate?: DcrPredicate;
    definitions?: MetricDefinition[];
  } = {},
): MetricTableRow[] {
  if (!response || response.data.length === 0) return [];
  const weekColumns = selectWeekColumns(response);

  const coverageRow: MetricTableRow = {
    ...dslRowPresentation(options.definitions, DSL_COVERAGE_PCT_KEY, {
      metric: 'DSL Coverage %',
      goal: DSL_COVERAGE_GOAL,
      goalDirection: 'GTE',
      unit: '%',
    }),
    isRate: true,
    derived: true,
    callout: '',
  };
  const avgRow: MetricTableRow = {
    ...dslRowPresentation(options.definitions, AVG_DCRS_PER_DSL_KEY, {
      metric: 'Avg DCRs per DSL',
      goal: AVG_DCRS_PER_DSL_GOAL,
      goalDirection: 'LTE',
      unit: '',
    }),
    isRate: false,
    derived: true,
    callout: '',
  };

  let seen = false;
  response.data.forEach((periodData, weekIndex) => {
    const weekKey = weekColumns[weekIndex].key;
    const dcrs = dcrsForNetwork(periodData, options.network, options.predicate);
    const total = new Set(dcrs.map((dcr) => dcr.dcrId)).size;
    const withOwner = new Set(
      dcrs.filter((dcr) => dslOwnerOf(dcr) !== null).map((dcr) => dcr.dcrId),
    ).size;
    const owners = new Set(
      dcrs.map(dslOwnerOf).filter((owner): owner is string => owner !== null),
    ).size;
    coverageRow[weekKey] = total > 0 ? (100 * withOwner) / total : null;
    // 1 decimal, matching the QuickSight number format (non-rate cells render
    // the value as-is).
    avgRow[weekKey] =
      owners > 0 ? Math.round((withOwner / owners) * 10) / 10 : null;
    if (total > 0) seen = true;
  });

  return seen ? [coverageRow, avgRow] : [];
}

// ──── Launch TAT rows (spec launch-tat-metrics) ────

/**
 * One row per AVERAGE / PERCENTILE catalog metric for the DCR Health section,
 * per week, from the per-DCR day counts in the same filtered slice as every
 * other cell. AVERAGE rows take the mean; PERCENTILE rows take PERCENTILE_DISC
 * at the selected percentile (default TP95, recomputed client-side).
 *
 * The catalog is the row skeleton: a row appears whenever the catalog defines
 * the metric, with null ("–") cells until values land. Adding, renaming or
 * retiring a Launch TAT variant is a catalog change only.
 */
export function selectLaunchTatRows(
  response: GetMetricsOutput | undefined,
  options: {
    network?: string;
    predicate?: DcrPredicate;
    definitions?: MetricDefinition[];
    /** Percentile for the TP row (0–100); defaults to TP95. */
    percentile?: number;
  } = {},
): MetricTableRow[] {
  if (!response || response.data.length === 0) return [];
  const percentile = options.percentile ?? DEFAULT_LAUNCH_TAT_PERCENTILE;
  const weekColumns = selectWeekColumns(response);
  const periods = filteredPeriods(response, options.network, options.predicate);

  const build = (definition: MetricDefinition): MetricTableRow => {
    const { metricKey } = definition;
    const reduce =
      definition.aggregationType === 'PERCENTILE'
        ? (values: number[]) => percentileDisc(values, percentile)
        : meanOf;
    const row: MetricTableRow = {
      metric: definition.name,
      metricKey,
      owner: definition.owner ?? '',
      goal: parseFloat(definition.goal ?? '0') || 0,
      goalDirection: (definition.goalDirection ?? 'LTE').toUpperCase(),
      // Calendar days — from the catalog once the backend serves unit
      // (hilt-wbr-api-serving 6.2.1); "days" fallback until it deploys.
      unit: definition.unit ?? 'days',
      isRate: false,
      callout: '',
    };
    periods.forEach((dcrs, weekIndex) => {
      row[weekColumns[weekIndex].key] = reduce(numericValues(dcrs, metricKey));
    });
    return row;
  };

  return (options.definitions ?? [])
    .filter(hasSpecialAggregation)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(build);
}

/**
 * Roll up the CURRENT week per network (DCR demandCategory) for the KPI
 * tiles page. Each tile lists that network's metrics with on/off-track
 * status vs their goals.
 */
export function selectNetworkTiles(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  predicate?: DcrPredicate,
  metricGroups?: MetricGroupSelection,
  metricNames?: MetricNameSelection,
): NetworkTile[] {
  if (!response || response.data.length === 0 || !definitions?.length)
    return [];
  const catalog = buildMetricCatalog(definitions);
  const current =
    response.data.find((period) => period.timeline === 'CURRENT') ??
    response.data[response.data.length - 1];
  const currentDcrs = predicate
    ? current.dcrData.filter(predicate)
    : current.dcrData;

  // Metric Group and Metric Name apply to every metric type — no percentage
  // carve-out (the legacy "OTD ignores the control" was a QuickSight
  // limitation, deliberately not copied).
  const orderedDefinitions = definitions
    // Non-SUM metrics (Launch TAT AVERAGE/PERCENTILE) never tile — a SUM of
    // day counts is meaningless.
    .filter((definition) => !hasSpecialAggregation(definition))
    .filter((definition) => metricGroupMatches(definition, metricGroups))
    .filter((definition) => metricNameMatches(definition.name, metricNames))
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Registry-driven (not data-driven): one tile per network in canonical order,
  // INCLUDING the rollups (Nova Network, Alexa Network) which aggregate their
  // member leaves via matchesDemandCategory. A DCR contributes to both its leaf
  // tile and its rollup tile. Networks with no matching DCRs this week are
  // skipped. Mirrors the Detailed Summary + the CTS dashboard's pivot set.
  // Every value any registry entry claims — used to detect an unmapped, non-null
  // demandCategory (a category the backend ships before this file learns it).
  const knownValues = new Set<string>();
  DEMAND_CATEGORIES.forEach((entry) =>
    entry.values.forEach((v) => knownValues.add(v)),
  );

  const tiles: NetworkTile[] = [];
  for (const entry of DEMAND_CATEGORIES) {
    // A DCR with no demandCategory — null, undefined, OR blank/whitespace —
    // buckets under Others (`?? 'Others'` alone misses '', which would drop the
    // DCR from every tile); known leaf values match their leaf + rollup entries.
    let dcrs = currentDcrs.filter((dcr) =>
      matchesDemandCategory(entry.id, dcr.demandCategory?.trim() || 'Others'),
    );
    // Others is the catch-all: also fold in DCRs whose non-empty demandCategory
    // matches NO registry entry, so a new backend category is never silently
    // dropped from the tiles (which would understate totals vs QuickSight). No
    // overlap with the matched set — those values are, by definition, unknown —
    // so a plain concat needs no dedup.
    if (entry.id === 'others') {
      const unmapped = currentDcrs.filter((dcr) => {
        const cat = dcr.demandCategory?.trim();
        return !!cat && !knownValues.has(cat);
      });
      dcrs = dcrs.concat(unmapped);
    }
    if (dcrs.length === 0) continue;
    let primaryRatePct = 100;
    let primaryFound = false;
    const tileMetrics: NetworkTileMetric[] = [];

    for (const definition of orderedDefinitions) {
      // Per-DCR carriers never surface on tiles.
      if (OTD_CARRIER_KEYS.has(definition.metricKey)) continue;
      const { value, isRate } = aggregateMetric(
        dcrs,
        definition.metricKey,
        catalog,
      );
      if (value === null) continue;
      const goal = parseFloat(definition.goal ?? '0') || 0;
      const higherIsBetter =
        (definition.goalDirection ?? 'GTE').toUpperCase() !== 'LTE';
      const isOnTrack =
        goal === 0 ? true : higherIsBetter ? value >= goal : value <= goal;
      tileMetrics.push({
        name: definition.name,
        isOnTrack,
        value: Math.round(value * 10) / 10,
        target: goal || undefined,
        unit: isRate ? '%' : undefined,
      });
      if (isRate && !primaryFound) {
        primaryRatePct = value;
        primaryFound = true;
      }
    }

    tiles.push({
      id: entry.id,
      name: entry.displayName,
      metrics: tileMetrics,
      primaryRatePct,
    });
  }

  return tiles;
}

// ──── Trend (spec: executive-summary-trends) ────

export interface MetricTrend {
  /** Weekly aggregated values, historical → current order, null weeks skipped. */
  values: number[];
  /** Week labels aligned to `values` (skipped weeks stay aligned). */
  weekLabels: string[];
  /** Current-week value (null when the week has no data). */
  current: number | null;
  /** Last historical week's value with data (null when none). */
  previous: number | null;
  /** Index of the anchor week within `values`; -1 when it has no data. */
  anchorIndex: number;
  /**
   * True when the series is a percentage (a rate metric, or a volume metric
   * whose trend is attainment) — callers format it with % and a goal line.
   */
  isRate: boolean;
}

/**
 * Derive the trend series for one pivoted row. Forward (forecast) weeks are
 * excluded — a trend is what happened, not what's planned. Pure function over
 * already-pivoted data, so filter changes recompute it for free (spec R1.5).
 */
export function selectMetricTrend(
  row: MetricTableRow,
  weeks: WeekColumn[],
  /** Key of the picker-selected (anchor/highlighted) week, when known. */
  anchorKey?: string,
): MetricTrend {
  // Volume rows with a base plot attainment (stored under attnKey); everyone
  // else plots the cell value itself. But only use attainment when the series
  // actually has values — a base declared in the catalog yet carrying no data
  // (or a zero denominator) leaves every attnKey null, which would render "–"
  // and silently lose the raw trend. Fall back to the raw series in that case
  // ("no usable base → raw-sum trend").
  const useAttainment =
    row.attainmentTrend === true &&
    // Probe only the NON-forward weeks — the same columns the values loop below
    // plots. Attainment present solely on a forward week would otherwise flip
    // this true while `values` stays empty, rendering "–" and losing the raw trend.
    weeks.some(
      (w) => w.group !== 'forward' && typeof row[attnKey(w.key)] === 'number',
    );
  const read = (weekKey: string) =>
    row[useAttainment ? attnKey(weekKey) : weekKey];

  const values: number[] = [];
  const weekLabels: string[] = [];

  for (const week of weeks) {
    if (week.group === 'forward') continue;
    const value = read(week.key);
    if (typeof value === 'number') {
      values.push(value);
      weekLabels.push(week.label);
    }
  }

  // The delta compares the week the user is LOOKING AT: the picker-selected
  // anchor when known, else the CURRENT-flagged week (live), else the last
  // non-forward week (snapshot captures carry no CURRENT flag).
  const nonForward = weeks.filter((w) => w.group !== 'forward');
  const anchorWeek =
    (anchorKey && nonForward.find((w) => w.key === anchorKey)) ||
    weeks.find((w) => w.group === 'current') ||
    nonForward[nonForward.length - 1];
  const currentRaw = anchorWeek ? read(anchorWeek.key) : undefined;
  const current = typeof currentRaw === 'number' ? currentRaw : null;

  // Latest week before the anchor with data, so a gap never reads as "no change".
  let previous: number | null = null;
  for (let i = nonForward.indexOf(anchorWeek) - 1; i >= 0; i--) {
    const value = read(nonForward[i].key);
    if (typeof value === 'number') {
      previous = value;
      break;
    }
  }

  // -1 when the anchor week plotted no value: the dot must not highlight a
  // stand-in week while the delta reads "no data".
  const anchorIndex = anchorWeek
    ? weekLabels.lastIndexOf(anchorWeek.label)
    : values.length - 1;
  return {
    values,
    weekLabels,
    current,
    previous,
    anchorIndex,
    isRate: useAttainment || row.isRate,
  };
}
